import { useState, useEffect, useRef, useContext } from 'react';
import { AuthContext } from 'contexts/AuthContext';
import chatService from 'ApiService/Student/chatService';

export default function StudentChatSystem() {
  const [selectedRole, setSelectedRole] = useState(null);
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const { state } = useContext(AuthContext);
  const studentId = state?.user?.id;

  const roles = [
    { id: 1, name: 'Admin', icon: '👑', color: 'bg-indigo-500' },
    { id: 2, name: 'Teacher', icon: '👨‍🏫', color: 'bg-green-500' },
    { id: 3, name: 'SuperAdmin', icon: '🔐', color: 'bg-purple-500' },
    { id: 4, name: 'Accountant', icon: '💼', color: 'bg-amber-500' },
    { id: 5, name: 'HR', icon: '👥', color: 'bg-pink-500' },
  ];

  useEffect(() => {
    if (studentId) {
      fetchStudentConversations();
    }
  }, [studentId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchStudentConversations = async () => {
    try {
      setLoading(true);
      const response = await chatService.getStudentConversations(studentId);
      setConversations(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersByRole = async (role) => {
    try {
      setLoading(true);
      const response = await chatService.fetchUsersByRole(role);
      setUsers(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setCurrentConversation(null);
    fetchUsersByRole(role.name);
  };

  const fetchConversation = async (conversationId) => {
    try {
      setLoading(true);
      const response = await chatService.getConversation(conversationId);
      setCurrentConversation(response.data.data);
      setMessages(response.data.data.messages);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching conversation');
    } finally {
      setLoading(false);
    }
  };

  const createOrGetConversation = async (recipientId, recipientRole) => {
    console.log(recipientId, recipientRole, 'recipientId, recipientRole');
    try {
      setLoading(true);
      const response = await chatService.createOrGetConversation(
        studentId,
        recipientId,
        recipientRole
      );

      await fetchConversation(response.data.data._id);
      fetchStudentConversations();
    } catch (err) {
      setError(err.response?.data?.message || 'Error creating conversation');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    const existingConv = conversations.find(
      (conv) =>
        conv.recipient._id === user._id &&
        conv.recipientModel === selectedRole.name
    );

    if (existingConv) {
      fetchConversation(existingConv._id);
    } else {
      createOrGetConversation(user._id, selectedRole.name);
    }
  };

  const handleConversationSelect = (conversation) => {
    fetchConversation(conversation._id);
  };

  const sendMessage = async () => {
    if (!messageInput.trim() || !currentConversation) return;

    try {
      setLoading(true);
      await chatService.sendMessage(
        currentConversation._id,
        messageInput.trim()
      );

      fetchConversation(currentConversation._id);
      setMessageInput('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error sending message');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const goBack = () => {
    if (currentConversation) {
      setCurrentConversation(null);
      setMessages([]);
    } else if (selectedRole) {
      setSelectedRole(null);
      setUsers([]);
    }
  };

  return (
    <div className="flex h-screen flex-col bg-gray-100 dark:bg-navy-800">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow dark:bg-navy-700">
        <div className="flex items-center">
          {(selectedRole || currentConversation) && (
            <button
              onClick={goBack}
              className="mr-4 text-gray-600 hover:text-gray-800"
            >
              ← Back
            </button>
          )}
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">
            {currentConversation
              ? `Chat with ${currentConversation.recipient.name}`
              : selectedRole
              ? `Select ${selectedRole.name}`
              : 'Who would you like to message?'}
          </h1>
        </div>
      </div>

      {/* Error Display */}
      {/* {error && (
        <div className="mx-6 mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
          <span className="block sm:inline">{error}</span>
          <span
            className="absolute bottom-0 right-0 top-0 px-4 py-3"
            onClick={() => setError(null)}
          >
            <svg
              className="fill-current h-6 w-6 text-red-500"
              role="button"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <title>Close</title>
              <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
            </svg>
          </span>
        </div>
      )} */}

      {/* Main Content */}
      <div className="flex-grow overflow-y-auto p-6">
        {!selectedRole && !currentConversation && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {roles.map((role) => (
              <div
                key={role.id}
                onClick={() => handleRoleSelect(role)}
                className={`${role.color} transform cursor-pointer rounded-xl p-6 text-white shadow-lg transition-transform hover:scale-105`}
              >
                <div className="flex items-center space-x-4">
                  <span className="text-4xl">{role.icon}</span>
                  <div>
                    <h2 className="text-xl font-bold">{role.name}</h2>
                    <p className="text-sm opacity-80">
                      Chat with {role.name.toLowerCase()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedRole && !currentConversation && (
          <div className="space-y-4">
            <h2 className="mb-4 text-lg font-medium text-gray-700">
              Select a {selectedRole.name}
            </h2>
            {loading ? (
              <div className="flex justify-center p-4">
                <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {users.map((user) => (
                  <div
                    key={user._id}
                    onClick={() => handleUserSelect(user)}
                    className=" flex cursor-pointer items-center rounded-lg bg-white p-4 shadow hover:bg-gray-50 dark:bg-brand-500"
                  >
                    <div className="mr-4 text-2xl ">
                      {user.profilePicture ? (
                        <img
                          src={user.profilePicture}
                          alt={user.name}
                          className="h-10 w-10 rounded-full object-cover dark:text-white"
                        />
                      ) : (
                        <span>{user.name.charAt(0)}</span>
                      )}
                    </div>
                    <div className="flex-grow">
                      <h3 className="font-medium dark:text-white">
                        {user.name}
                      </h3>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                ))}

                {users.length === 0 && !loading && (
                  <div className="py-8 text-center text-gray-500">
                    No {selectedRole.name} users found
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {currentConversation && (
          <div className="flex h-full flex-col">
            {/* Chat messages */}
            <div className="mb-4 flex-grow space-y-4 overflow-y-auto">
              {messages.length === 0 ? (
                <div className="rounded-lg bg-gray-200 p-3 text-center text-gray-500">
                  No messages yet. Send a message to start the conversation.
                </div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg._id}
                    className={`flex ${
                      msg.senderModel === 'Student'
                        ? 'justify-end'
                        : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-xs rounded-lg p-3 md:max-w-md ${
                        msg.senderModel === 'Student'
                          ? 'rounded-br-none bg-blue-500 text-white'
                          : 'rounded-bl-none border bg-white  text-gray-800 dark:bg-navy-800 dark:text-white'
                      }`}
                    >
                      {msg.senderModel !== 'Student' && (
                        <div className="mb-1 text-sm font-medium text-gray-500">
                          {currentConversation.recipient.name}
                        </div>
                      )}
                      <p>{msg.content}</p>
                      <div
                        className={`mt-1 text-xs ${
                          msg.senderModel === 'Student'
                            ? 'text-blue-100'
                            : 'text-gray-400'
                        }`}
                      >
                        {formatDate(msg.createdAt)}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}
      </div>

      {/* Chat input (only visible when a user is selected) */}
      {currentConversation && (
        <div className="border-t bg-white p-4  dark:bg-navy-800">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage();
            }}
            className="flex items-center"
          >
            <input
              type="text"
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              placeholder="Type your message..."
              className="flex-grow rounded-l-lg border p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-900 dark:text-white"
            />
            <button
              type="submit"
              disabled={loading || !messageInput.trim()}
              className={`${
                loading || !messageInput.trim()
                  ? 'cursor-not-allowed bg-blue-300'
                  : 'bg-blue-500 hover:bg-blue-600'
              } rounded-r-lg p-2 text-white`}
            >
              {loading ? '...' : 'Send'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
